/**
 * Heatmap style overrides.
 *
 * Applies color gradient derived from palette + selected color index,
 * slider visibility (vertical on the right side), cell value labels
 * with formatting, and cell border radius.
 */
import type { EChartsOption } from 'echarts';
import type { TypeStyle, PaletteOption } from '../../../../../edit/visualConfig/types';
import { getHeatmapColorGradient } from '../../../../../edit/visualConfig/palettes';
import { isSingleObject } from '../helpers';
import { formatChartValue } from '../../../../../../utils/formatters';

export function applyHeatmapOverrides(
  option: EChartsOption,
  cs: TypeStyle,
  palette?: PaletteOption,
): EChartsOption {
  const result = { ...option };
  const showSlider = cs.showSlider ?? false;
  const colorIndex = cs.heatmapColorIndex ?? 0;
  const gradientColors = getHeatmapColorGradient(palette ?? 'default', colorIndex);

  // Color gradient + slider visibility via visualMap
  if (isSingleObject(result.visualMap)) {
    const vm = result.visualMap as any;
    vm.inRange = { color: gradientColors };
    vm.show = showSlider;

    if (showSlider) {
      // Position slider vertically on the right — saves bottom space
      vm.orient = 'vertical';
      vm.right = 0;
      vm.top = 'center';
      vm.text = [vm.max != null ? formatChartValue(vm.max) : '', vm.min != null ? formatChartValue(vm.min) : ''];
      vm.textGap = 12;
      delete vm.bottom;
      delete vm.left;
    }
  }

  // Adjust grid: generous right margin when slider is visible
  if (isSingleObject(result.grid)) {
    const grid = result.grid as any;
    grid.bottom = 60;
    grid.right = showSlider ? 110 : 30;
  }

  // Cell-level overrides: label formatter + border radius
  if (Array.isArray(result.series)) {
    result.series = (result.series as any[]).map((s: any) => {
      if (s.type !== 'heatmap') return s;
      return {
        ...s,
        label: {
          ...s.label,
          formatter: (params: any) => {
            const raw = Array.isArray(params.value) ? params.value[2] : params.value;
            const num = typeof raw === 'number' ? raw : Number(raw);
            if (isNaN(num)) return String(raw ?? '');
            return formatChartValue(num);
          },
        },
        itemStyle: { ...s.itemStyle, borderRadius: cs.cellRadius ?? 0 },
      };
    });
  }

  return result;
}